/* eslint-disable react/jsx-no-bind */
import { useEffect, useRef, useState } from 'react'
import { Download } from 'lucide-react'
import { LuX } from 'react-icons/lu'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from 'services/api'
import { Button, Modal } from '@cannect/new-components/atoms'
import { TModalActions } from '@cannect/new-components/atoms/Modal'
import {
  TRecipeAccessDocument,
  TRecipePrescription,
  TRecipeProducts,
  TRecipeRealPatient,
  TValidateRecipeAccessSuccess,
  useGetBaseRecipe
} from '@cannect/services/resources/recipeAccess'
import Loading from 'components/Loading'
import { AccessCodeModal } from './AccessCodeModal'
import DocumentsListModal from './DocumentsListModal'
import { ReceiptCard } from './ReceiptCard'
import { RecipeAccessScreen } from './RecipeAccessScreen'
import { RecipeErrorBoundary } from './RecipeErrorBoundary'
import { RecipeProductsScreen } from './RecipeProductsScreen'
import { RecipeUnavailableScreen } from './RecipeUnavailableScreen'
import { useRecipeBrandColors } from './useRecipeBrandColors'
import * as Style from './styles'

export type BaseRecipePathParams = {
  token: string
}

// Formato que o `DocumentsListModal` (compartilhado) espera — compatível
// com `TRecipeAccessDocument` (mesmos campos; `verification_code` opcional).
export type RecipeDocumentType = 'simple' | 'exam' | 'rce' | 'antimicrobial'

export type RecipeDocument = {
  type: RecipeDocumentType
  token: string
  url: string
  label: string
  verification_code?: string
}

type ValidatedData = {
  patient: TRecipeRealPatient
  products: TRecipeProducts
  prescription: TRecipePrescription
  emission: string
  documents: TRecipeAccessDocument[]
}

type RecipeViewState =
  | { kind: 'expired' }
  | { kind: 'phone_invalid' }
  | ({ kind: 'recipe' } & ValidatedData)
  | ({ kind: 'products' } & ValidatedData)

// Intenção de quando o paciente abriu o modal de código (decisão D1 item 3,
// spec visual 19/10): "Ver receita" e "Ver produtos" usam o MESMO modal; a
// diferença é só pra onde a validação leva depois.
type AccessIntent = 'recipe' | 'products'

export default function BaseRecipe() {
  const { token } = useParams<BaseRecipePathParams>()
  const history = useHistory()
  const colors = useRecipeBrandColors()
  const { data, isLoading, isError, error } = useGetBaseRecipe(token)

  const [validated, setValidated] = useState<RecipeViewState | null>(null)
  const [isAccessCodeModalOpen, setIsAccessCodeModalOpen] = useState(false)
  const [accessIntent, setAccessIntent] = useState<AccessIntent>('recipe')
  const [fetchedDocuments, setFetchedDocuments] = useState<RecipeDocument[]>([])
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false)
  const [isOpeningCart, setIsOpeningCart] = useState(false)
  const receiptModalRef = useRef<TModalActions>(null)

  const isReceiptModalOpen = validated?.kind === 'recipe'

  useEffect(() => {
    if (isReceiptModalOpen) receiptModalRef.current?.open()
    else receiptModalRef.current?.close()
  }, [isReceiptModalOpen])

  function openAccessCodeModal(intent: AccessIntent) {
    setAccessIntent(intent)
    setIsAccessCodeModalOpen(true)
  }

  function goToHelp() {
    history.push(`/receitas/${token}/ajuda`)
  }

  // Achado 50 (Lince): o `Baixar PDF` novo usa a URL assinada de `documents`
  // (decisão 42) em vez do fluxo antigo por PIN (`PasswordModal`, removido —
  // ficou sem chamador). Mas uma receita pode ter MAIS de um documento (ex.
  // simples + controlada) — o `DocumentsListModal` antigo já resolvia isso
  // listando todos; reuso ele aqui em vez de baixar só o primeiro.
  function handleOpenDocuments(documents: TRecipeAccessDocument[]) {
    if (documents.length === 0) return

    if (documents.length === 1) {
      window.open(documents[0].url, '_blank', 'noopener,noreferrer')
      return
    }

    setFetchedDocuments(documents.map((document) => ({ ...document, type: document.type as RecipeDocumentType })))
    setIsDocumentsModalOpen(true)
  }

  function openCartUrl(cartPath: string, reservedWindow?: Window | null) {
    const cartUrl = `${window.location.origin}${cartPath}`
    if (reservedWindow && !reservedWindow.closed) {
      reservedWindow.opener = null
      reservedWindow.location.replace(cartUrl)
      return
    }
    const opened = window.open(cartUrl, '_blank', 'noopener,noreferrer')
    if (!opened) {
      toast.error('Permita pop-ups para abrir o carrinho numa nova janela.')
    }
  }

  async function handleOpenCart(reservedWindow?: Window | null) {
    try {
      setIsOpeningCart(true)
      const { data: orderData } = await api.post(`/order_recipe/${token}`)
      const cartId = orderData?.order?.uuid

      if (!cartId) {
        reservedWindow?.close()
        toast.error('Não foi possível abrir o carrinho. Tente novamente.')
        return
      }

      openCartUrl(`/carrinho-wa?cart_id=${cartId}`, reservedWindow)
    } catch (err) {
      reservedWindow?.close()
      toast.error('Não foi possível abrir o carrinho. Tente novamente.')
    } finally {
      setIsOpeningCart(false)
    }
  }

  function handleValidated(result: TValidateRecipeAccessSuccess, reservedWindow?: Window | null) {
    setIsAccessCodeModalOpen(false)

    if (accessIntent === 'products') {
      if (!result.products.available) {
        reservedWindow?.close()
        setValidated({
          kind: 'products',
          patient: result.patient,
          products: result.products,
          prescription: result.prescription,
          emission: result.recipe.emission,
          documents: result.documents
        })
        return
      }

      void handleOpenCart(reservedWindow)
      return
    }

    reservedWindow?.close()
    setValidated({
      kind: 'recipe',
      patient: result.patient,
      products: result.products,
      prescription: result.prescription,
      emission: result.recipe.emission,
      documents: result.documents
    })
  }

  function renderBody() {
    if (isLoading) return null

    if (validated?.kind === 'products') {
      return (
        <RecipeProductsScreen
          products={validated.products}
          token={token}
          emission={validated.emission}
          prescriberName={validated.prescription.prescriber?.name}
          onContinue={() => handleOpenCart()}
          onBack={() => setValidated(null)}
          isContinueLoading={isOpeningCart}
        />
      )
    }

    if (validated?.kind === 'phone_invalid') return <RecipeUnavailableScreen reason="phone_invalid" />

    if (validated?.kind === 'expired' || isError) {
      const status = (error as unknown as { response?: { status?: number } } | undefined)?.response?.status
      if (!validated && status !== 404) {
        // Erro diferente de "token inexistente" (ex.: rede/5xx): mantém a
        // mensagem genérica de hoje em vez de "link expirado", que seria
        // enganoso.
        return <p>Não foi possível localizar a receita informada</p>
      }
      return <RecipeUnavailableScreen reason="expired" />
    }

    if (!data) return <p>Não foi possível localizar a receita informada</p>

    // Decisão 14: celular ausente/não normalizável -> não libera a tela de
    // código, oferece contato.
    if (!data.patient.phone) return <RecipeUnavailableScreen reason="phone_invalid" />

    // Estado "recipe" (D3) renderiza como modal (ver abaixo) por cima desta
    // mesma tela D1 — por isso ela continua no fundo enquanto o modal está
    // aberto, exatamente como no protótipo desktop.
    return (
      <RecipeAccessScreen
        patient={data.patient}
        prescriber={data.prescriber}
        token={token}
        emission={data.recipe.emission}
        onRequestAccess={() => openAccessCodeModal('recipe')}
        onRequestProducts={() => openAccessCodeModal('products')}
        onRequestHelp={goToHelp}
      />
    )
  }

  return (
    <RecipeErrorBoundary token={token}>
      <Style.BaseRecipeContainer>
        <Style.ContentWrapper>{renderBody()}</Style.ContentWrapper>

        {(isLoading || isOpeningCart) && <Loading loading />}

        {data?.patient && (
          <AccessCodeModal
            isOpen={isAccessCodeModalOpen}
            token={token}
            patient={data.patient}
            onRequestClose={() => setIsAccessCodeModalOpen(false)}
            onValidated={handleValidated}
            reserveNewWindowOnSubmit={accessIntent === 'products'}
            onTokenExpired={() => {
              setIsAccessCodeModalOpen(false)
              setValidated({ kind: 'expired' })
            }}
          />
        )}

        {/* D3 — receita aberta (modal reusado, mesmo componente do D2) */}
        <Modal
          ref={receiptModalRef}
          hiddenCloseButton
          classNames={{ containerClassName: 'w-full max-w-full p-5 sm:max-w-[560px] sm:p-7' }}>
          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-950">Sua receita</h2>
              <Button unstyled type="button" aria-label="Fechar" onClick={() => setValidated(null)}>
                <LuX className="text-gray-800" size={24} />
              </Button>
            </div>

            {validated?.kind === 'recipe' && (
              <>
                <ReceiptCard
                  patient={validated.patient}
                  emission={validated.emission}
                  token={token}
                  prescription={validated.prescription}
                />
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    icon={<Download size={18} />}
                    iconPlacement="left"
                    disabled={validated.documents.length === 0}
                    onClick={() => handleOpenDocuments(validated.documents)}
                    className={`h-[54px] flex-grow rounded-[110px] text-base text-white ${colors.cta}`}>
                    Baixar PDF
                  </Button>
                  <Button
                    unstyled
                    onClick={() => setValidated(null)}
                    className="h-[54px] flex-grow rounded-[110px] border border-gray-100 text-base font-semibold text-gray-950">
                    Voltar à receita
                  </Button>
                </div>
              </>
            )}
          </div>
        </Modal>

        {isDocumentsModalOpen && (
          <DocumentsListModal
            isOpen={isDocumentsModalOpen}
            documents={fetchedDocuments}
            onRequestClose={() => setIsDocumentsModalOpen(false)}
          />
        )}
      </Style.BaseRecipeContainer>
    </RecipeErrorBoundary>
  )
}
